import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { mockLeads, mockConnectors, mockSMContacts, mockTeamMembers, SMContact, TeamMember, BroadcastGroup, BroadcastTemplate, mockBroadcastGroups, mockBroadcastTemplates } from "@/data/mockData";
import { getLegacyCustomerName } from "@/types";
import { getConnectors, getSMContacts, getBroadcastGroups, saveBroadcastGroups, getBroadcastTemplates, saveBroadcastTemplates } from "@/lib/localStorage";
import { ArrowLeft, Search, Send, Paperclip, Users, UserCircle, X, Image, FileText, MessageCircle, Phone, FolderPlus, Edit2, Trash2, Save, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  file?: File;
}

interface Contact {
  id: string;
  name: string;
  phone: string;
  type: 'customer' | 'partner' | 'sm' | 'team';
}

export default function BroadcastPage() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<'quick' | 'compose' | 'groups' | 'templates'>('quick');
  const [contactTab, setContactTab] = useState<'customers' | 'partners' | 'sm' | 'team' | 'groups'>('customers');
  const [sendMethod, setSendMethod] = useState<'whatsapp' | 'sms'>('whatsapp');
  
  // Groups and Templates
  const [groups, setGroups] = useState<BroadcastGroup[]>([]);
  const [templates, setTemplates] = useState<BroadcastTemplate[]>([]);
  
  // Dialog states
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<BroadcastGroup | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<BroadcastTemplate | null>(null);
  
  // Form states
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupContacts, setNewGroupContacts] = useState<{ id: string; type: 'customer' | 'partner' | 'sm' | 'team' }[]>([]);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateMessage, setNewTemplateMessage] = useState("");
  
  // Load connectors and Banker contacts from localStorage
  const [connectors, setConnectors] = useState(mockConnectors);
  const [smContacts, setSmContacts] = useState<SMContact[]>(mockSMContacts);

  useEffect(() => {
    const storedConnectors = getConnectors();
    if (storedConnectors && storedConnectors.length > 0) {
      setConnectors(storedConnectors);
    }
    
    const storedSM = getSMContacts();
    if (storedSM && storedSM.length > 0) {
      setSmContacts(storedSM);
    }
    
    const storedGroups = getBroadcastGroups();
    if (storedGroups && storedGroups.length > 0) {
      setGroups(storedGroups);
    } else {
      setGroups(mockBroadcastGroups);
      saveBroadcastGroups(mockBroadcastGroups);
    }
    
    const storedTemplates = getBroadcastTemplates();
    if (storedTemplates && storedTemplates.length > 0) {
      setTemplates(storedTemplates);
    } else {
      setTemplates(mockBroadcastTemplates);
      saveBroadcastTemplates(mockBroadcastTemplates);
    }
  }, []);

  // Save groups and templates
  useEffect(() => {
    if (groups.length > 0) saveBroadcastGroups(groups);
  }, [groups]);
  
  useEffect(() => {
    if (templates.length > 0) saveBroadcastTemplates(templates);
  }, [templates]);

  // Convert leads to contacts
  const customerContacts: Contact[] = mockLeads.map(lead => ({
    id: lead.id,
    name: getLegacyCustomerName(lead),
    phone: lead.mobile,
    type: 'customer' as const,
  }));

  // Convert connectors to contacts
  const partnerContacts: Contact[] = connectors.map(c => ({
    id: c.id,
    name: c.name,
    phone: c.whatsappNumber,
    type: 'partner' as const,
  }));

  // Banker contacts
  const smContactList: Contact[] = smContacts.map(c => ({
    id: c.id,
    name: c.name,
    phone: c.phone,
    type: 'sm' as const,
  }));

  // Team members contacts
  const teamContactList: Contact[] = mockTeamMembers.map(tm => ({
    id: tm.id,
    name: tm.name,
    phone: tm.phone,
    type: 'team' as const,
  }));

  const allContacts = [...customerContacts, ...partnerContacts, ...smContactList, ...teamContactList];

  const getCurrentContacts = () => {
    if (contactTab === 'groups') return [];
    if (contactTab === 'customers') return customerContacts;
    if (contactTab === 'partners') return partnerContacts;
    if (contactTab === 'team') return teamContactList;
    return smContactList;
  };

  const currentContacts = getCurrentContacts();

  const filteredContacts = currentContacts.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone.includes(searchQuery)
  );

  const handleToggleContact = (contact: Contact) => {
    setSelectedContacts(prev => {
      const exists = prev.find(c => c.id === contact.id && c.type === contact.type);
      if (exists) {
        return prev.filter(c => !(c.id === contact.id && c.type === contact.type));
      }
      return [...prev, contact];
    });
  };

  const handleSelectGroup = (group: BroadcastGroup) => {
    const groupContacts = group.contactIds.map(ci => {
      const found = allContacts.find(c => c.id === ci.id && c.type === ci.type);
      return found;
    }).filter(Boolean) as Contact[];
    
    setSelectedContacts(prev => {
      const newContacts = groupContacts.filter(gc =>
        !prev.some(p => p.id === gc.id && p.type === gc.type)
      );
      return [...prev, ...newContacts];
    });
    toast.success(`Added ${groupContacts.length} contacts from "${group.name}"`);
  };

  const handleSelectAll = () => {
    const allFilteredSelected = filteredContacts.every(c =>
      selectedContacts.some(sc => sc.id === c.id && sc.type === c.type)
    );
    
    if (allFilteredSelected) {
      setSelectedContacts(prev =>
        prev.filter(sc => !filteredContacts.some(c => c.id === sc.id && c.type === sc.type))
      );
    } else {
      setSelectedContacts(prev => {
        const newContacts = filteredContacts.filter(c =>
          !prev.some(sc => sc.id === c.id && sc.type === sc.type)
        );
        return [...prev, ...newContacts];
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newAttachments: Attachment[] = Array.from(files).map(file => ({
        id: `att_${Date.now()}_${file.name}`,
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const handleSendWhatsApp = (contact: Contact) => {
    const encodedMessage = encodeURIComponent(message);
    const phone = contact.phone.replace(/\D/g, "");
    const url = `https://wa.me/91${phone}?text=${encodedMessage}`;
    window.open(url, "_blank");
  };

  const playNotificationSound = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleUcQMXGyw9ygbT0OKYPU4NquZisJKYPV4NquZisJ');
    audio.play().catch(() => {});
  };

  const handleSendBroadcast = () => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    if (selectedContacts.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }

    if (sendMethod === 'whatsapp') {
      // Send to all selected contacts with staggered timing
      console.log(`Sending to ${selectedContacts.length} contacts:`, selectedContacts);
      
      selectedContacts.forEach((contact, index) => {
        setTimeout(() => {
          console.log(`Opening WhatsApp for contact ${index + 1}:`, contact.name);
          handleSendWhatsApp(contact);
        }, index * 500); // 500ms delay between each contact
      });
      
      playNotificationSound();
      
      toast.success(`Opening WhatsApp for ${selectedContacts.length} contacts`, {
        description: "Please send the message in each WhatsApp window",
        duration: 5000,
      });
    } else {
      playNotificationSound();
      toast.success(`Broadcast sent via SMS!`, {
        description: `Sent to ${selectedContacts.length} contacts`,
      });
    }

    // Clear form after sending
    setMessage("");
    setSubject("");
    setAttachments([]);
    setSelectedContacts([]);
  };

  const handleUseTemplate = (template: BroadcastTemplate) => {
    setMessage(template.message);
    setActiveTab('compose');
    toast.success(`Template "${template.name}" loaded`);
  };

  // Group CRUD
  const handleSaveGroup = () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter group name");
      return;
    }
    if (newGroupContacts.length === 0) {
      toast.error("Please select at least one contact");
      return;
    }

    if (editingGroup) {
      setGroups(prev => prev.map(g => 
        g.id === editingGroup.id 
          ? { ...g, name: newGroupName, contactIds: newGroupContacts }
          : g
      ));
      toast.success("Group updated");
    } else {
      const newGroup: BroadcastGroup = {
        id: `bg${Date.now()}`,
        name: newGroupName,
        contactIds: newGroupContacts,
        createdBy: "Current User",
        createdAt: new Date(),
      };
      setGroups(prev => [...prev, newGroup]);
      toast.success("Group created");
    }
    
    setCreateGroupDialogOpen(false);
    setEditingGroup(null);
    setNewGroupName("");
    setNewGroupContacts([]);
  };

  const handleEditGroup = (group: BroadcastGroup) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setNewGroupContacts(group.contactIds);
    setCreateGroupDialogOpen(true);
  };

  const handleDeleteGroup = (groupId: string) => {
    setGroups(prev => prev.filter(g => g.id !== groupId));
    toast.success("Group deleted");
  };

  // Template CRUD
  const handleSaveTemplate = () => {
    if (!newTemplateName.trim() || !newTemplateMessage.trim()) {
      toast.error("Please fill all fields");
      return;
    }

    if (editingTemplate) {
      setTemplates(prev => prev.map(t => 
        t.id === editingTemplate.id 
          ? { ...t, name: newTemplateName, message: newTemplateMessage }
          : t
      ));
      toast.success("Template updated");
    } else {
      const newTemplate: BroadcastTemplate = {
        id: `bt${Date.now()}`,
        name: newTemplateName,
        message: newTemplateMessage,
        createdBy: "Current User",
        createdAt: new Date(),
      };
      setTemplates(prev => [...prev, newTemplate]);
      toast.success("Template saved");
    }
    
    setCreateTemplateDialogOpen(false);
    setEditingTemplate(null);
    setNewTemplateName("");
    setNewTemplateMessage("");
  };

  const handleEditTemplate = (template: BroadcastTemplate) => {
    setEditingTemplate(template);
    setNewTemplateName(template.name);
    setNewTemplateMessage(template.message);
    setCreateTemplateDialogOpen(true);
  };

  const handleDeleteTemplate = (templateId: string) => {
    setTemplates(prev => prev.filter(t => t.id !== templateId));
    toast.success("Template deleted");
  };

  const handleToggleGroupContact = (contactId: string, contactType: 'customer' | 'partner' | 'sm' | 'team') => {
    setNewGroupContacts(prev => {
      const exists = prev.some(c => c.id === contactId && c.type === contactType);
      if (exists) {
        return prev.filter(c => !(c.id === contactId && c.type === contactType));
      }
      return [...prev, { id: contactId, type: contactType }];
    });
  };

  const isSelected = (contact: Contact) =>
    selectedContacts.some(sc => sc.id === contact.id && sc.type === contact.type);

  const customerCount = selectedContacts.filter(c => c.type === 'customer').length;
  const partnerCount = selectedContacts.filter(c => c.type === 'partner').length;
  const smCount = selectedContacts.filter(c => c.type === 'sm').length;
  const teamCount = selectedContacts.filter(c => c.type === 'team').length;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto pb-24 md:pb-8">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/tools")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeader title="Broadcast" subtitle="Send messages to multiple contacts" />
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'compose' | 'groups' | 'templates')}>
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger 
              value="quick"
              className="data-[state=active]:bg-success data-[state=active]:text-success-foreground"
            >
              <MessageCircle className="h-4 w-4 mr-1 hidden md:inline" />
              Quick
            </TabsTrigger>
            <TabsTrigger 
              value="compose"
              className="data-[state=active]:bg-success data-[state=active]:text-success-foreground"
            >
              <Send className="h-4 w-4 mr-1 hidden md:inline" />
              Compose
            </TabsTrigger>
            <TabsTrigger 
              value="groups"
              className="data-[state=active]:bg-success data-[state=active]:text-success-foreground"
            >
              <Users className="h-4 w-4 mr-1 hidden md:inline" />
              Groups
            </TabsTrigger>
            <TabsTrigger 
              value="templates"
              className="data-[state=active]:bg-success data-[state=active]:text-success-foreground"
            >
              <FileText className="h-4 w-4 mr-1 hidden md:inline" />
              Templates
            </TabsTrigger>
          </TabsList>

          {/* Quick Tab - Simplified Team Messaging */}
          <TabsContent value="quick">
            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
              {/* Message Composer - Simplified */}
              <Card variant="elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <MessageCircle className="h-5 w-5 text-success" />
                    Quick Team Message
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="quick-message">Message *</Label>
                    <Textarea
                      id="quick-message"
                      placeholder="Type your message here..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={8}
                      className="resize-none"
                    />
                  </div>

                  <Button 
                    onClick={handleSendBroadcast} 
                    className="w-full bg-success hover:bg-success/90"
                    disabled={!message.trim() || selectedContacts.length === 0}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Send via WhatsApp ({selectedContacts.length})
                  </Button>
                </CardContent>
              </Card>

              {/* Team Recipients */}
              <Card variant="elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Users className="h-5 w-5 text-success" />
                    Team Members
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search team members..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Select All */}
                  <div className="flex items-center justify-between">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        const teamContactList: Contact[] = mockTeamMembers.map(member => ({
                          id: member.id,
                          name: member.name,
                          phone: member.phone,
                          type: 'team' as const
                        }));
                        
                        const filteredTeam = teamContactList.filter(c =>
                          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.phone.includes(searchQuery)
                        );
                        
                        if (filteredTeam.every(c => isSelected(c))) {
                          // Deselect all team members
                          setSelectedContacts(prev => prev.filter(sc => 
                            !filteredTeam.some(fc => fc.id === sc.id)
                          ));
                        } else {
                          // Select all team members
                          const newSelections = filteredTeam.filter(c => !isSelected(c));
                          setSelectedContacts(prev => [...prev, ...newSelections]);
                        }
                      }}
                    >
                      {(() => {
                        const teamContactList: Contact[] = mockTeamMembers.map(member => ({
                          id: member.id,
                          name: member.name,
                          phone: member.phone,
                          type: 'team' as const
                        }));
                        
                        const filteredTeam = teamContactList.filter(c =>
                          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.phone.includes(searchQuery)
                        );
                        
                        return filteredTeam.every(c => isSelected(c)) && filteredTeam.length > 0
                          ? "Deselect All"
                          : "Select All";
                      })()}
                    </Button>
                    <Badge variant="outline" className="bg-success/10 text-success border-success">
                      {(() => {
                        const teamContactList: Contact[] = mockTeamMembers.map(member => ({
                          id: member.id,
                          name: member.name,
                          phone: member.phone,
                          type: 'team' as const
                        }));
                        
                        const filteredTeam = teamContactList.filter(c =>
                          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.phone.includes(searchQuery)
                        );
                        
                        return `${filteredTeam.filter(c => isSelected(c)).length}/${filteredTeam.length}`;
                      })()}
                    </Badge>
                  </div>

                  {/* Team Contact List */}
                  <div className="max-h-[600px] md:max-h-[700px] overflow-y-auto space-y-2">
                    {mockTeamMembers
                      .filter(member =>
                        member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        member.phone.includes(searchQuery)
                      )
                      .map((member) => {
                        const contact: Contact = {
                          id: member.id,
                          name: member.name,
                          phone: member.phone,
                          type: 'team'
                        };
                        return (
                          <motion.div
                            key={member.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer hover:bg-secondary"
                            onClick={() => handleToggleContact(contact)}
                          >
                            <Checkbox
                              checked={isSelected(contact)}
                              onCheckedChange={() => {}}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{member.name}</div>
                              <div className="text-xs text-muted-foreground truncate">{member.phone}</div>
                            </div>
                            {member.hasAppAccess && (
                              <Badge variant="outline" className="text-xs bg-success/10 text-success border-success">
                                App User
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-success flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendWhatsApp(contact);
                              }}
                            >
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                          </motion.div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="compose">
            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
              {/* Message Composer */}
              <Card variant="elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Send className="h-5 w-5" />
                    Compose Message
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Send Method Toggle */}
                  <div className="flex gap-2">
                    <Button
                      variant={sendMethod === 'whatsapp' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSendMethod('whatsapp')}
                      className="flex-1"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                    <Button
                      variant={sendMethod === 'sms' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSendMethod('sms')}
                      className="flex-1"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      SMS
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Subject (Optional)</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g., Diwali Offer, New Scheme Launch"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Message *</Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type your message here..."
                      rows={5}
                      className="resize-none"
                    />
                  </div>

                  {/* Quick Templates */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Quick Templates</Label>
                    <div className="flex flex-wrap gap-2">
                      {templates.slice(0, 3).map(template => (
                        <Button
                          key={template.id}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => handleUseTemplate(template)}
                        >
                          {template.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Attachments */}
                  <div className="space-y-2">
                    <Label>Attachments</Label>
                    <div className="flex flex-wrap gap-2">
                      {attachments.map(att => (
                        <Badge key={att.id} variant="secondary" className="flex items-center gap-1 py-1.5 pr-1">
                          {att.type.startsWith('image/') ? (
                            <Image className="h-3 w-3" />
                          ) : (
                            <FileText className="h-3 w-3" />
                          )}
                          <span className="max-w-[80px] truncate text-xs">{att.name}</span>
                          <button 
                            onClick={() => handleRemoveAttachment(att.id)}
                            className="ml-1 p-0.5 hover:bg-muted rounded"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <label className="cursor-pointer inline-block">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          <Paperclip className="h-4 w-4 mr-2" />
                          Add Attachment
                        </span>
                      </Button>
                    </label>
                  </div>

                  {/* Selected Recipients Summary */}
                  {selectedContacts.length > 0 && (
                    <div className="p-3 bg-secondary rounded-lg">
                      <p className="text-sm font-medium">
                        Selected: {selectedContacts.length} recipients
                      </p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {customerCount > 0 && (
                          <Badge variant="info" className="text-xs">
                            {customerCount} Customers
                          </Badge>
                        )}
                        {partnerCount > 0 && (
                          <Badge variant="accent" className="text-xs">
                            {partnerCount} Partners
                          </Badge>
                        )}
                        {smCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {smCount} Bankers
                          </Badge>
                        )}
                        {teamCount > 0 && (
                          <Badge variant="success" className="text-xs">
                            {teamCount} Team
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={handleSendBroadcast} 
                    className="w-full"
                    disabled={!message.trim() || selectedContacts.length === 0}
                  >
                    {sendMethod === 'whatsapp' ? (
                      <MessageCircle className="h-4 w-4 mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send via {sendMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'} ({selectedContacts.length})
                  </Button>
                </CardContent>
              </Card>

              {/* Contact Selection */}
              <Card variant="elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Users className="h-5 w-5" />
                    Select Recipients
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Tabs value={contactTab} onValueChange={(v) => setContactTab(v as 'customers' | 'partners' | 'sm' | 'team' | 'groups')}>
                    <TabsList className="w-full flex h-auto items-center justify-start rounded-md bg-muted p-1 text-muted-foreground flex-wrap gap-1">
                      <TabsTrigger 
                        value="customers" 
                        className="text-xs flex-1 min-w-0 data-[state=active]:bg-success data-[state=active]:text-success-foreground"
                      >
                        Customers
                      </TabsTrigger>
                      <TabsTrigger 
                        value="partners" 
                        className="text-xs flex-1 min-w-0 data-[state=active]:bg-success data-[state=active]:text-success-foreground"
                      >
                        Partners
                      </TabsTrigger>
                      <TabsTrigger 
                        value="sm" 
                        className="text-xs flex-1 min-w-0 data-[state=active]:bg-success data-[state=active]:text-success-foreground"
                      >
                        Banker
                      </TabsTrigger>
                      <TabsTrigger 
                        value="team" 
                        className="text-xs flex-1 min-w-0 data-[state=active]:bg-success data-[state=active]:text-success-foreground"
                      >
                        Team
                      </TabsTrigger>
                      <TabsTrigger 
                        value="groups" 
                        className="text-xs flex-1 min-w-0 data-[state=active]:bg-success data-[state=active]:text-success-foreground"
                      >
                        Groups
                      </TabsTrigger>
                    </TabsList>

                    <div className="mt-4 space-y-3">
                      {contactTab !== 'groups' && (
                        <>
                          {/* Search */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search by name or phone..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="pl-10"
                            />
                          </div>

                          {/* Select All */}
                          <div className="flex items-center justify-between">
                            <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                              {filteredContacts.every(c => isSelected(c)) && filteredContacts.length > 0
                                ? "Deselect All"
                                : "Select All"}
                            </Button>
                            <Badge variant="outline">
                              {filteredContacts.filter(c => isSelected(c)).length}/{filteredContacts.length}
                            </Badge>
                          </div>

                          {/* Contact List */}
                          <div className="max-h-[600px] md:max-h-[700px] overflow-y-auto space-y-2">
                            {filteredContacts.map((contact, index) => (
                              <motion.div
                                key={`${contact.type}-${contact.id}`}
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.02 }}
                                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-secondary"
                                onClick={() => handleToggleContact(contact)}
                              >
                                <Checkbox 
                                  checked={isSelected(contact)}
                                  onCheckedChange={() => {}}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate text-sm">{contact.name}</p>
                                  <p className="text-xs text-muted-foreground">{contact.phone}</p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-success flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSendWhatsApp(contact);
                                  }}
                                >
                                  <MessageCircle className="h-4 w-4" />
                                </Button>
                              </motion.div>
                            ))}

                            {filteredContacts.length === 0 && (
                              <p className="text-center py-8 text-muted-foreground text-sm">
                                No contacts found
                              </p>
                            )}
                          </div>
                        </>
                      )}

                      {contactTab === 'groups' && (
                        <div className="max-h-[400px] overflow-y-auto space-y-2">
                          {groups.map(group => (
                            <div 
                              key={group.id} 
                              className="p-3 rounded-lg border hover:bg-secondary cursor-pointer flex items-center justify-between"
                              onClick={() => handleSelectGroup(group)}
                            >
                              <div>
                                <p className="font-medium text-sm">{group.name}</p>
                                <p className="text-xs text-muted-foreground">{group.contactIds.length} contacts</p>
                              </div>
                              <Button size="sm" variant="outline">
                                <Plus className="h-3 w-3 mr-1" />
                                Add All
                              </Button>
                            </div>
                          ))}
                          {groups.length === 0 && (
                            <p className="text-center py-8 text-muted-foreground text-sm">
                              No groups created yet
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="groups">
            <Card>
              <CardHeader className="flex-row items-center justify-between pb-3">
                <CardTitle className="text-base">Broadcast Groups</CardTitle>
                <Button size="sm" onClick={() => { setEditingGroup(null); setNewGroupName(""); setNewGroupContacts([]); setCreateGroupDialogOpen(true); }}>
                  <FolderPlus className="h-4 w-4 mr-1" />
                  Create Group
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {groups.map(group => (
                    <motion.div
                      key={group.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg border flex items-center justify-between"
                    >
                      <div>
                        <p className="font-semibold">{group.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {group.contactIds.length} contacts • Created by {group.createdBy}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="icon" variant="ghost" onClick={() => handleEditGroup(group)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteGroup(group.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                  {groups.length === 0 && (
                    <p className="text-center py-8 text-muted-foreground">
                      No groups created yet. Create your first group to quickly broadcast to multiple contacts.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates">
            <Card>
              <CardHeader className="flex-row items-center justify-between pb-3">
                <CardTitle className="text-base">Message Templates</CardTitle>
                <Button size="sm" onClick={() => { setEditingTemplate(null); setNewTemplateName(""); setNewTemplateMessage(""); setCreateTemplateDialogOpen(true); }}>
                  <Save className="h-4 w-4 mr-1" />
                  Create Template
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {templates.map(template => (
                    <motion.div
                      key={template.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg border"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-semibold">{template.name}</p>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleUseTemplate(template)}>
                            Use
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleEditTemplate(template)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteTemplate(template.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{template.message}</p>
                    </motion.div>
                  ))}
                  {templates.length === 0 && (
                    <p className="text-center py-8 text-muted-foreground">
                      No templates created yet. Save frequently used messages as templates.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Group Dialog */}
        <Dialog open={createGroupDialogOpen} onOpenChange={setCreateGroupDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Broadcast Group'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Group Name *</Label>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., Top Partners, VIP Customers"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Select Contacts ({newGroupContacts.length} selected)</Label>
                <div className="max-h-[300px] overflow-y-auto border rounded-lg p-2 space-y-1">
                  {allContacts.map(contact => (
                    <div
                      key={`${contact.type}-${contact.id}`}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                        newGroupContacts.some(c => c.id === contact.id && c.type === contact.type)
                          ? "bg-primary/10"
                          : "hover:bg-secondary"
                      )}
                      onClick={() => handleToggleGroupContact(contact.id, contact.type)}
                    >
                      <Checkbox 
                        checked={newGroupContacts.some(c => c.id === contact.id && c.type === contact.type)} 
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{contact.name}</p>
                        <p className="text-xs text-muted-foreground">{contact.phone}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">{contact.type}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              <Button onClick={handleSaveGroup} className="w-full">
                {editingGroup ? 'Update Group' : 'Create Group'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Template Dialog */}
        <Dialog open={createTemplateDialogOpen} onOpenChange={setCreateTemplateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Message Template'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="e.g., Diwali Wishes, New Offer"
                />
              </div>
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  value={newTemplateMessage}
                  onChange={(e) => setNewTemplateMessage(e.target.value)}
                  placeholder="Type your template message here..."
                  rows={5}
                />
              </div>
              <Button onClick={handleSaveTemplate} className="w-full">
                {editingTemplate ? 'Update Template' : 'Save Template'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
